import { useEffect, useMemo, useState } from 'react';

import { notify } from '../../app';
import { OpenAPI } from '../../api';
import { getUserOrgs } from '../../api';
import { useUserSettings } from './useUserSettings';
import type { UserOrganizationDto } from '../../api';
import { OpenAPI as ScraperOpenAPI } from '../../services/scraper';
import { OpenAPI as CompressorOpenAPI } from '../../services/compressor';
import { OpenAPI as AgentCallOpenAPI } from '../../services/call-management';
import { OpenAPI as KnowledgeManagement } from '../../services/knowledge-base';
import { OpenAPI as WidgetAnalyticsApi } from '../../services/widget-analytics';
import { OpenAPI as ConversationsBilling } from '../../services/conversations-billing';
import { OpenAPI as ConversationsBillingV2 } from '../../services/conversations-billing-v2';
import { OpenAPI as ConversationParticipants } from '../../services/conversation-participants';
import { OpenAPI as ShortLink } from '../../services/short-link';
import { OpenAPI as AccessManagement } from '../../services/access-management';
import { OpenAPI as AgentsManagement } from '../../services/agents-management';
import { OpenAPI as PromptLibrary } from '../../services/prompt-library';
import { OpenAPI as VaultAuthSecret } from '../../services/vault-auth';
import { OpenAPI as PresentationsManagement } from '../../services/presentations-management';

const extractPemissions = (
  organizations: UserOrganizationDto[],
  selectedOrg?: string
): SpPermission[] => {
  if (!selectedOrg) return [];

  const organization = organizations?.find(o => o.id === selectedOrg);
  if (!organization) return [];

  return Array.from(
    new Set(organization.roles?.flatMap?.(r => r.permissions ?? []) ?? [])
  ) as SpPermission[];
};

export const useSetPermissions = (
  token: string,
  loading: boolean,
  onRelogin: () => void
) => {
  const [inited, setInited] = useState(false);

  const [orgs, setOrgs] = useState<UserOrganizationDto[]>([]);
  const [selectedOrg, handleSetSelectedOrg] = useUserSettings<
    string | undefined
  >('', 'sp-admin-selected-org');

  const setSelectedOrg = (newOrg?: string) => {
    handleSetSelectedOrg(newOrg);
    // setTimeout(() => window.location.replace('/'), 0);
  };

  useEffect(() => {
    const orgHeaders = {
      'x-ai-agent-organization-context': selectedOrg || '',
    };

    OpenAPI.HEADERS = orgHeaders;
    ScraperOpenAPI.HEADERS = orgHeaders;
    CompressorOpenAPI.HEADERS = orgHeaders;
    AgentCallOpenAPI.HEADERS = orgHeaders;
    KnowledgeManagement.HEADERS = orgHeaders;
    WidgetAnalyticsApi.HEADERS = orgHeaders;
    ConversationsBilling.HEADERS = orgHeaders;
    ConversationsBillingV2.HEADERS = orgHeaders;
    ConversationParticipants.HEADERS = orgHeaders;
    ShortLink.HEADERS = orgHeaders;
    AccessManagement.HEADERS = orgHeaders;
    AgentsManagement.HEADERS = orgHeaders;
    PromptLibrary.HEADERS = orgHeaders;
    VaultAuthSecret.HEADERS = orgHeaders;
    PresentationsManagement.HEADERS = orgHeaders;
  }, [selectedOrg]);

  useEffect(
    () => {
      if (loading) return;
      if (!token) return setInited(true);

      let ignore = false;

      const processUserInfo = async () => {
        try {
          const response = await getUserOrgs();
          const organizations = response?.organizations ?? [];

          if (ignore) return;

          setOrgs(organizations);

          if (!organizations.some(o => o.id === selectedOrg)) {
            handleSetSelectedOrg(organizations[0]?.id ?? '');
          }

          setInited(true);
        } catch {
          if (ignore) return;

          setOrgs([]);
          setInited(false);
          onRelogin?.();
          notify.error('Failed to get user info');
        }
      };

      processUserInfo();

      return () => {
        ignore = true;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, loading]
  );

  const permissions = useMemo(
    () => extractPemissions(orgs, selectedOrg),
    [orgs, selectedOrg]
  );

  return {
    inited,
    orgs,
    setSelectedOrg,
    permissions: permissions,
    organization_id: selectedOrg,
  };
};
